import { Helmet } from 'react-helmet-async';
import { useState, useEffect } from 'react';
import axios from 'axios';

// @mui
import { Container, Stack, Typography, Grid, Chip, Box, Paper, alpha, Skeleton } from '@mui/material';
// components
import PokemonCard from '../sections/@dashboard/pokemons/PokemonCard';
import PokemonFilterSidebar from '../sections/@dashboard/pokemons/PokemonFilterSidebar';
import PokemonSearch from '../sections/@dashboard/pokemons/PokemonSearch';
import api from '../services/api';
import '../utils/style.css';
// ----------------------------------------------------------------------

export default function PokedexPage() {
  const [openFilter, setOpenFilter] = useState(false);

  const handleOpenFilter = () => {
    setOpenFilter(true);
  };

  const handleCloseFilter = () => {
    setOpenFilter(false);
  };

  // Função unificada para aplicar todos os filtros
  const applyFilters = (search, types, generation) => {
    let filtered = [...allPokemons];

    // Filtro por busca (nome ou ID)
    if (search && search.trim() !== '') {
      filtered = filtered.filter((pokemon) => 
        pokemon.data.name.toLowerCase().includes(search.toLowerCase()) ||
        pokemon.data.id.toString().includes(search)
      );
    }

    // Filtro por tipos (múltiplos)
    if (types && types.length > 0) {
      filtered = filtered.filter((pokemon) => 
        pokemon.data.types.some((typeSlot) => 
          types.includes(typeSlot.type.name.toLowerCase())
        )
      );
    }

    // Filtro por geração
    if (generation && generation !== '') {
      filtered = filtered.filter((pokemon) => 
        pokemon.generation === generation
      );
    }

    setPokemons(filtered);
  };

  const handlePokemonsFilter = (typesSelected, generationSelected) => {
    const newTypes = typesSelected || [];
    const newGeneration = generationSelected || '';
    
    setSelectedTypes(newTypes);
    setSelectedGeneration(newGeneration);
    
    applyFilters(searchTerm, newTypes, newGeneration);
  };

  const [pokemons, setPokemons] = useState([]);
  const [allPokemons, setAllPokemons] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedGeneration, setSelectedGeneration] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getPokemons();
  }, []);

  async function getPokemons() {
    setLoading(true);
    const endpoints = [];

    // Carregando todas as 8 gerações (Pokémon 1 a 905)
    for (let index = 1; index < 906; index += 1) {
      endpoints.push(`pokemon/${index}/`);
    }
    
    try {
      const pokemonResponses = await axios.all(endpoints.map((endpoint) => api.get(endpoint)));
      
      // Buscar as gerações de todos os Pokémons
      const speciesEndpoints = pokemonResponses.map((pokemon) => 
        `pokemon-species/${pokemon.data.id}/`
      );
      const speciesResponses = await axios.all(speciesEndpoints.map((endpoint) => api.get(endpoint)));
      
      // Adicionar a geração a cada pokémon
      const pokemonsWithGeneration = pokemonResponses.map((pokemon, index) => ({
        ...pokemon,
        generation: speciesResponses[index].data.generation.name,
      }));
      
      setAllPokemons(pokemonsWithGeneration);
      setPokemons(pokemonsWithGeneration);
    } catch (err) {
      // Error handling silently
    } finally {
      setLoading(false);
    }
  }

  const handleSearchChange = (searchValue) => {
    setSearchTerm(searchValue);
    applyFilters(searchValue, selectedTypes, selectedGeneration);
  };

  const handleRemoveTypeFilter = (typeToRemove) => {
    const newTypes = selectedTypes.filter(t => t !== typeToRemove);
    setSelectedTypes(newTypes);
    applyFilters(searchTerm, newTypes, selectedGeneration);
  };

  const handleRemoveGenerationFilter = () => {
    setSelectedGeneration('');
    applyFilters(searchTerm, selectedTypes, '');
  };

  const getGenerationLabel = (gen) => {
    const genMap = {
      'generation-i': 'Gen I',
      'generation-ii': 'Gen II',
      'generation-iii': 'Gen III',
      'generation-iv': 'Gen IV',
      'generation-v': 'Gen V',
      'generation-vi': 'Gen VI',
      'generation-vii': 'Gen VII',
      'generation-viii': 'Gen VIII',
    };
    return genMap[gen] || gen;
  };

  return (
    <>
      <Helmet>
        <title> Dashboard: Pokedex </title>
      </Helmet>

      <Container maxWidth="xl">
        {/* Header com estilo melhorado */}
        <Box sx={{ mb: { xs: 3, sm: 4, md: 5 } }}>
          <Stack direction="row" alignItems="center" spacing={{ xs: 1, sm: 2 }} sx={{ mb: { xs: 1, sm: 2 } }}>
            <Typography 
              variant="h3" 
              sx={{ 
                fontWeight: 700,
                fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' },
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Pokédex
            </Typography>
            {!loading && (
              <Chip
                label={`${pokemons.length}`}
                color="primary"
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: '0.7rem', sm: '0.875rem' },
                  height: { xs: 24, sm: 32 },
                }}
              />
            )}
          </Stack>
          <Typography 
            variant="body1" 
            sx={{ 
              color: 'text.secondary',
              fontSize: { xs: '0.875rem', sm: '1rem' },
            }}
          >
            {loading 
              ? 'Loading Pokémon from all 8 generations...' 
              : allPokemons.length > 0 && pokemons.length < allPokemons.length
                ? `Showing ${pokemons.length} of ${allPokemons.length} Pokémon`
                : `Explore all ${allPokemons.length} Pokémon from 8 generations`}
          </Typography>
        </Box>

        {/* Barra de Ferramentas */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: { xs: 2, sm: 3 },
            bgcolor: 'background.paper',
            borderRadius: 2,
            border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={10}>
              <PokemonSearch onSearchChange={handleSearchChange} />
            </Grid>
            <Grid item xs={12} md={2}>
              <PokemonFilterSidebar
                openFilter={openFilter}
                onOpenFilter={handleOpenFilter}
                onCloseFilter={handleCloseFilter}
                onTypeChange={handlePokemonsFilter}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Filtros Ativos */}
        {(selectedTypes.length > 0 || selectedGeneration) && (
          <Box sx={{ mb: { xs: 2, sm: 3 } }}>
            <Stack direction="row" spacing={{ xs: 0.5, sm: 1 }} flexWrap="wrap" alignItems="center" gap={{ xs: 0.5, sm: 1 }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'text.secondary', 
                  fontWeight: 600, 
                  mr: { xs: 0.5, sm: 1 },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                }}
              >
                Active:
              </Typography>
              {selectedTypes.map((type) => (
                <Chip
                  key={type}
                  label={type}
                  onDelete={() => handleRemoveTypeFilter(type)}
                  color="primary"
                  variant="filled"
                  size="small"
                  sx={{ 
                    fontWeight: 600,
                    textTransform: 'capitalize',
                    fontSize: { xs: '0.7rem', sm: '0.8125rem' },
                    height: { xs: 24, sm: 32 },
                  }}
                />
              ))}
              {selectedGeneration && (
                <Chip
                  label={getGenerationLabel(selectedGeneration)}
                  onDelete={handleRemoveGenerationFilter}
                  color="secondary"
                  variant="filled"
                  size="small"
                  sx={{ 
                    fontWeight: 600,
                    fontSize: { xs: '0.7rem', sm: '0.8125rem' },
                    height: { xs: 24, sm: 32 },
                  }}
                />
              )}
            </Stack>
          </Box>
        )}

        {/* Grid de Pokémons com animação */}
        <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
          {loading ? (
            // Loading Skeletons
            Array.from(new Array(12)).map((_, index) => (
              <Grid item xs={4} sm={6} md={4} lg={3} xl={2} key={index}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 1, sm: 1.5, md: 2 },
                    borderRadius: 2,
                    bgcolor: (theme) => alpha(theme.palette.background.paper, 0.8),
                    border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  }}
                >
                  <Skeleton variant="circular" width={{ xs: 50, sm: 70, md: 80 }} height={{ xs: 50, sm: 70, md: 80 }} sx={{ mx: 'auto', mb: 1 }} />
                  <Skeleton variant="text" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' }, mb: 0.5 }} />
                  <Stack direction="row" spacing={0.5} justifyContent="center">
                    <Skeleton variant="rounded" width={{ xs: 40, sm: 55, md: 60 }} height={{ xs: 20, sm: 22, md: 24 }} />
                    <Skeleton variant="rounded" width={{ xs: 40, sm: 55, md: 60 }} height={{ xs: 20, sm: 22, md: 24 }} />
                  </Stack>
                </Paper>
              </Grid>
            ))
          ) : pokemons.length > 0 ? (
            pokemons.map((pokemon, key) => (
              <Grid 
                item 
                xs={4} 
                sm={6} 
                md={4} 
                lg={3} 
                xl={2} 
                key={key}
                className="fade-in-up"
                style={{ animationDelay: `${(key % 12) * 0.05}s` }}
              >
                <PokemonCard
                  id={pokemon.data.id}
                  name={pokemon.data.name}
                  image={pokemon.data.sprites.front_default}
                  types={pokemon.data.types}
                  generation={pokemon.generation}
                />
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 8,
                  textAlign: 'center',
                  bgcolor: (theme) => alpha(theme.palette.background.paper, 0.5),
                  backdropFilter: 'blur(10px)',
                  borderRadius: 3,
                  border: (theme) => `1px dashed ${alpha(theme.palette.divider, 0.3)}`,
                }}
              >
                <Box
                  sx={{
                    width: 120,
                    height: 120,
                    mx: 'auto',
                    mb: 3,
                    borderRadius: '50%',
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography variant="h1" sx={{ fontSize: 64 }}>
                    🔍
                  </Typography>
                </Box>
                <Typography variant="h5" sx={{ color: 'text.primary', mb: 1, fontWeight: 600 }}>
                  No Pokémon Found
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                  Try adjusting your filters or search terms
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      </Container>
    </>
  );
}
